import React from 'react';
import { Typography } from 'antd';
import type { EvidenceRequirementViewV0 } from '@shared/operator-read-models';
import {
  PRIMITIVE_AUTHORITY_FLAGS,
  type PrimitivePresentationState,
} from './primitiveSurfaceState';
import { PrimitiveSurfaceFrame } from './PrimitiveSurfaceFrame';
import styles from './primitives.module.css';

const { Text, Paragraph } = Typography;

export interface EvidenceSummaryProps {
  readonly presentationState: PrimitivePresentationState;
  /** R0 EvidenceRequirementView — never infer satisfied from attachment. */
  readonly requirement?: EvidenceRequirementViewV0 | null;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

function satisfiedDisplay(satisfied: boolean | null): string {
  if (satisfied === true) return 'SATISFIED';
  if (satisfied === false) return 'UNSATISFIED';
  return 'PARTIAL/UNKNOWN';
}

export function EvidenceSummary(props: EvidenceSummaryProps): React.ReactElement {
  const grantsAuthority = props.grantsAuthority ?? PRIMITIVE_AUTHORITY_FLAGS.grantsAuthority;
  const grantsApproval = props.grantsApproval ?? PRIMITIVE_AUTHORITY_FLAGS.grantsApproval;
  if (grantsAuthority !== false || grantsApproval !== false) {
    throw new Error('EvidenceSummary must keep grantsAuthority/grantsApproval=false');
  }

  const requirement = props.requirement;
  const attachedCount = requirement?.attachedInstanceIds.length ?? 0;
  const admissibleCount = requirement?.admissibleInstanceIds.length ?? 0;
  const satisfied = requirement?.satisfied ?? null;

  return (
    <PrimitiveSurfaceFrame
      title="증빙 요약"
      presentationState={props.presentationState}
      emptyMessage="증빙 요구사항이 없습니다."
      partialMessage="증빙 충족 여부가 불완전합니다 (PARTIAL/UNKNOWN)."
      data-testid={props['data-testid'] ?? 'evidence-summary'}
      aria-label="증빙 요약"
    >
      {requirement ? (
        <div className={styles.primitiveStack}>
          <div className={styles.primitiveFieldRow}>
            <span className={styles.primitiveFieldLabel}>요구사항</span>
            <Text>
              {requirement.requirementId} ({requirement.evidenceType})
            </Text>
          </div>
          <div className={styles.primitiveFieldRow}>
            <span className={styles.primitiveFieldLabel}>required</span>
            <Text>{requirement.required ? 'true' : 'false'}</Text>
          </div>
          <div className={styles.attachedBlock} data-testid="evidence-attached-block">
            <span className={styles.primitiveFieldLabel}>attached (부착)</span>
            <Text>
              {attachedCount} · {requirement.attachedInstanceIds.join(', ') || '없음'}
            </Text>
          </div>
          <div className={styles.primitiveFieldRow}>
            <span className={styles.primitiveFieldLabel}>admissible</span>
            <Text>
              {admissibleCount} · {requirement.admissibleInstanceIds.join(', ') || '없음'}
            </Text>
          </div>
          <div className={styles.satisfiedBlock} data-testid="evidence-satisfied-block">
            <span className={styles.primitiveFieldLabel}>satisfied (서버)</span>
            <Text strong data-satisfied={String(satisfied)}>
              {satisfiedDisplay(satisfied)}
            </Text>
            <Text type="secondary">부착 수({attachedCount})로 충족을 추론하지 않습니다.</Text>
          </div>
          <Paragraph data-testid="evidence-attachment-not-sufficiency" style={{ marginBottom: 0 }}>
            Attachment is not sufficiency
          </Paragraph>
          <div className={styles.primitiveFieldRow}>
            <span className={styles.primitiveFieldLabel}>satisfiedBasis</span>
            <Text>{requirement.satisfiedBasis ?? '없음'}</Text>
          </div>
          <div className={styles.primitiveFieldRow}>
            <span className={styles.primitiveFieldLabel}>status</span>
            <Text>{requirement.status}</Text>
          </div>
          {requirement.blockingReason ? (
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>blockingReason</span>
              <Text>{requirement.blockingReason}</Text>
            </div>
          ) : null}
          {satisfied === null ? (
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              satisfied=null → PARTIAL/UNKNOWN (완료로 표시하지 않음)
            </Paragraph>
          ) : null}
        </div>
      ) : (
        <Text type="secondary">EvidenceRequirementView 없음</Text>
      )}
    </PrimitiveSurfaceFrame>
  );
}
